import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Collapse,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Save as SaveIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import {
  calculateAMMBasedDistribution,
  exportToCSV,
  isValidNumber
} from '../utils/dynamicTokenDistributionCalculator';
import { ethereumService } from '../utils/ethereum';
import { useProject } from '../projects/ProjectContext';

const ERC20_META_ABI = [
  'function totalSupply() view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const TokenDistributionCalculator = () => {
  const navigate = useNavigate();
  const { currentProjectId, projectSettings } = useProject();

  const assignedTokenAddr = useMemo(
    () => (projectSettings?.token_address || projectSettings?.tokenAddress || '').toString().trim(),
    [projectSettings],
  );
  const tokenSymbolDisplay = useMemo(
    () => (projectSettings?.token_symbol || projectSettings?.tokenSymbol || '').toString().trim(),
    [projectSettings],
  );
  const decimalsHint = useMemo(() => {
    const d = Number(projectSettings?.token_decimals ?? projectSettings?.tokenDecimals);
    return Number.isFinite(d) && d >= 0 && d <= 36 ? d : 18;
  }, [projectSettings]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(true);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFilename, setExportFilename] = useState('distribution_results.csv');
  /** Count of bundle wallets (GET /wallets) for this project; null until first fetch after a plan exists. */
  const [bundleWalletCount, setBundleWalletCount] = useState(null);
  const [bundleWalletCountLoading, setBundleWalletCountLoading] = useState(false);

  /** false until settings load settles (avoid flashing “no token”) */
  const [projectTokenCheckDone, setProjectTokenCheckDone] = useState(false);
  const [hasProjectToken, setHasProjectToken] = useState(false);
  /** Token exists but RPC / contract read failed */
  const [tokenReadError, setTokenReadError] = useState(null);
  const [poolSupplyLoading, setPoolSupplyLoading] = useState(false);

  const [formData, setFormData] = useState({
    totalSupply: '',
    initialEthPool: '',
    initialTokenPool: '',
    minPercentage: '',
    maxPercentage: '',
    slippageTolerance: '0.5',
    targetPercentage: '100',
    gasBufferPercentage: '2',
    fundingAmount: ''
  });

  /**
   * Uses project token address from ProjectContext (`/api/me/settings`).
   * Intentionally does not depend on Clerk `getToken` identity — that was causing an infinite reload loop.
   */
  const reloadPoolSupplyFromChain = useCallback(async () => {
    setTokenReadError(null);

    if (!currentProjectId) {
      setHasProjectToken(false);
      setProjectTokenCheckDone(true);
      setPoolSupplyLoading(false);
      setFormData((prev) => ({ ...prev, totalSupply: '', initialTokenPool: '' }));
      return;
    }

    const addr = assignedTokenAddr;
    if (!addr || !ethers.utils.isAddress(addr)) {
      setHasProjectToken(false);
      setProjectTokenCheckDone(true);
      setPoolSupplyLoading(false);
      setFormData((prev) => ({ ...prev, totalSupply: '', initialTokenPool: '' }));
      return;
    }

    setHasProjectToken(true);
    setPoolSupplyLoading(true);
    try {
      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;
      const c = new ethers.Contract(addr, ERC20_META_ABI, provider);

      let decimals = decimalsHint;
      try {
        const d = await c.decimals();
        const n = Number(d);
        if (Number.isFinite(n) && n >= 0 && n <= 36) decimals = n;
      } catch {
        decimals = decimalsHint;
      }

      let supWei;
      try {
        supWei = await c.totalSupply();
      } catch (err) {
        const msg = err?.reason || err?.message || String(err);
        throw new Error(msg || 'Could not read totalSupply from token contract');
      }
      const human = ethers.utils.formatUnits(supWei, decimals);
      setFormData((prev) => ({ ...prev, totalSupply: human, initialTokenPool: human }));
    } catch (e) {
      const msg = e?.message || 'Failed to load project token supply';
      setTokenReadError(msg);
      setFormData((prev) => ({ ...prev, totalSupply: '', initialTokenPool: '' }));
      setHasProjectToken(true);
    } finally {
      setPoolSupplyLoading(false);
      setProjectTokenCheckDone(true);
    }
  }, [currentProjectId, assignedTokenAddr, decimalsHint]);

  useEffect(() => {
    void reloadPoolSupplyFromChain();
  }, [reloadPoolSupplyFromChain]);

  const computed = useMemo(() => {
    const v = (x) => (isValidNumber(x) ? Number(x) : null);
    const totalSupply = v(formData.totalSupply);
    const initialEthPool = v(formData.initialEthPool);
    const initialTokenPool = v(formData.initialTokenPool);
    const minPercentage = v(formData.minPercentage);
    const maxPercentage = v(formData.maxPercentage);
    const slippageTolerance = v(formData.slippageTolerance);
    const targetPercentage = v(formData.targetPercentage);
    const gasBufferPercentage = v(formData.gasBufferPercentage);
    const fundingAmount = formData.fundingAmount ? v(formData.fundingAmount) : null;

    const initialPrice =
      totalSupply && initialEthPool != null && initialTokenPool
        ? initialEthPool / initialTokenPool
        : null;

    return {
      totalSupply,
      initialEthPool,
      initialTokenPool,
      minPercentage,
      maxPercentage,
      slippageTolerance,
      targetPercentage,
      gasBufferPercentage,
      fundingAmount,
      initialPrice,
    };
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateInputs = () => {
    const {
      totalSupply,
      initialEthPool,
      initialTokenPool,
      minPercentage,
      maxPercentage,
      slippageTolerance,
      targetPercentage,
      gasBufferPercentage,
      fundingAmount
    } = formData;

    if (!hasProjectToken) {
      toast.error('Deploy a token for this project first.');
      return false;
    }
    if (tokenReadError) {
      toast.error('Reload token supply (Retry) before calculating.');
      return false;
    }
    if (!isValidNumber(totalSupply)) {
      toast.error('Token supply is not loaded yet — check RPC or Retry.');
      return false;
    }
    if (!isValidNumber(initialEthPool)) {
      toast.error('Please enter a valid initial ETH pool amount');
      return false;
    }
    if (!isValidNumber(initialTokenPool)) {
      toast.error('Token side of the pool must match supply (reload from chain)');
      return false;
    }
    if (!isValidNumber(minPercentage) || minPercentage < 0 || minPercentage > 100) {
      toast.error('Please enter a valid minimum percentage (0-100)');
      return false;
    }
    if (!isValidNumber(maxPercentage) || maxPercentage > 100 || parseFloat(maxPercentage) < parseFloat(minPercentage)) {
      toast.error('Please enter a valid maximum percentage (>= min, <= 100)');
      return false;
    }
    if (!isValidNumber(slippageTolerance)) {
      toast.error('Please enter a valid slippage tolerance');
      return false;
    }
    if (!isValidNumber(targetPercentage) || targetPercentage > 100) {
      toast.error('Please enter a valid target percentage (0-100)');
      return false;
    }
    if (!isValidNumber(gasBufferPercentage)) {
      toast.error('Please enter a valid gas buffer percentage');
      return false;
    }
    if (fundingAmount && !isValidNumber(fundingAmount)) {
      toast.error('Please enter a valid funding amount (or leave blank)');
      return false;
    }

    return true;
  };

  const handleCalculate = async () => {
    if (!validateInputs()) return;

    setLoading(true);
    setError(null);

    try {
      const {
        totalSupply,
        initialEthPool,
        initialTokenPool,
        minPercentage,
        maxPercentage,
        targetPercentage
      } = formData;

      const distribution = calculateAMMBasedDistribution(
        parseFloat(totalSupply),
        parseFloat(initialEthPool),
        parseFloat(initialTokenPool),
        parseFloat(minPercentage),
        parseFloat(maxPercentage),
        parseFloat(targetPercentage)
      );

      setResults(distribution);
      setResultsOpen(true);
      toast.success('Plan calculated');
    } catch (error) {
      console.error('Error calculating distribution:', error);
      setError(error.message);
      toast.error('Failed to calculate plan: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshBundleWalletCount = useCallback(async () => {
    if (!currentProjectId) {
      setBundleWalletCount(null);
      return;
    }
    setBundleWalletCountLoading(true);
    try {
      await ethereumService.ensureInitialized();
      const w = await ethereumService.loadWallets();
      setBundleWalletCount(Array.isArray(w) ? w.length : 0);
    } catch {
      setBundleWalletCount(0);
    } finally {
      setBundleWalletCountLoading(false);
    }
  }, [currentProjectId]);

  useEffect(() => {
    if (results && currentProjectId) void refreshBundleWalletCount();
  }, [results, currentProjectId, refreshBundleWalletCount]);

  const handleGenerateMissingBundleWallets = async () => {
    if (!results) return;
    const needed = Math.max(0, parseInt(results.numWallets, 10) || 0);
    if (!needed) return;

    setLoading(true);
    try {
      await ethereumService.ensureInitialized();
      const wallets = await ethereumService.loadWallets();
      const existing = Array.isArray(wallets) ? wallets : [];
      const shortfall = needed - existing.length;
      if (shortfall <= 0) {
        toast.info(`This project already has at least ${needed} bundle wallet(s). You can apply the plan.`);
        setBundleWalletCount(existing.length);
        return;
      }
      const merged = [...existing];
      for (let i = 0; i < shortfall; i += 1) {
        const w = ethers.Wallet.createRandom();
        merged.push({
          address: w.address,
          privateKey: w.privateKey,
          balance: '0.00',
        });
      }
      const saveRes = await ethereumService.saveWallets(merged);
      setBundleWalletCount(merged.length);
      if (!saveRes?.success) {
        toast.warning(
          `Created ${shortfall} new wallet(s) in the browser; server did not persist (${saveRes?.error || 'unknown'}). Check Wallet Management.`,
        );
      } else {
        toast.success(
          `Created ${shortfall} new bundle wallet(s) (${merged.length} total for this project). Apply the plan when ready.`,
        );
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to generate wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!results) return;

    try {
      const {
        totalSupply,
        initialEthPool,
        initialTokenPool
      } = formData;

      exportToCSV(
        results,
        parseFloat(totalSupply),
        parseFloat(initialEthPool),
        parseFloat(initialTokenPool),
        exportFilename
      );
      toast.success('Results exported successfully');
      setShowExportDialog(false);
    } catch (error) {
      console.error('Error exporting results:', error);
      toast.error('Failed to export results: ' + error.message);
    }
  };

  const handleUpdateWallets = async () => {
    if (!results) return;

    try {
      setLoading(true);
      const gasBufferPercentage = parseFloat(formData.gasBufferPercentage || '0');
      const fundingAmountValue = formData.fundingAmount ? parseFloat(formData.fundingAmount) : null;
      const hasCustomFunding = typeof fundingAmountValue === 'number' && Number.isFinite(fundingAmountValue) && fundingAmountValue > 0;

      // Load existing wallets only — never mint new keys here (Wallet Management owns wallet creation).
      const wallets = await ethereumService.loadWallets();
      const existing = Array.isArray(wallets) ? wallets : [];

      const desiredCount = Math.max(0, parseInt(results.numWallets, 10) || 0);
      if (!desiredCount) {
        toast.error('This plan has no wallet slots to apply.');
        return;
      }
      if (!existing.length) {
        toast.error(
          'No bundle wallets for this project yet. Use “Generate … new bundle wallet(s)” in Plan actions, or add wallets in Wallet Management, then apply again.',
        );
        return;
      }
      if (existing.length < desiredCount) {
        toast.error(
          `This plan targets ${desiredCount} wallets but Wallet Management only has ${existing.length}. ` +
            'Add wallets there first — Launch Planner never creates keys.',
        );
        return;
      }

      const base = existing.slice(0, desiredCount);

      const updated = base.map((w, idx) => {
        const baseEthAmount = results.wallets[idx]?.eth ?? 0;
        const gasBuffer = baseEthAmount * (gasBufferPercentage / 100);
        return {
          ...w,
          buyAmount: baseEthAmount.toFixed(9),
          // fundAmount is the TOTAL amount to send to the wallet.
          // If a static funding amount is provided, treat it as extra (gas/overhead) and add it to buyAmount.
          fundAmount: (baseEthAmount + (hasCustomFunding ? fundingAmountValue : gasBuffer)).toFixed(9),
        };
      });

      const saveRes = await ethereumService.saveWallets(updated);
      if (!saveRes?.success) {
        toast.warning(`Saved locally (server didn’t persist): ${saveRes?.error || 'unknown error'}`);
      }

      toast.success(
        existing.length > desiredCount
          ? `Applied plan to the first ${desiredCount} of ${existing.length} wallets (buy + funding amounts)`
          : `Updated ${desiredCount} wallets with buy + funding amounts`,
      );
      void refreshBundleWalletCount();
    } catch (error) {
      console.error('Error updating wallets:', error);
      toast.error('Failed to update wallets: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const plannerDisabled = !hasProjectToken || !!tokenReadError || poolSupplyLoading;

  if (!currentProjectId) {
    return (
      <Box>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Select a project first, then deploy a token. Launch Planner uses your deployed token&apos;s supply and assumes{' '}
          <strong>100%</strong> of it is added as the token side of initial liquidity.
        </Alert>
      </Box>
    );
  }

  if (!projectTokenCheckDone) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Checking this project&apos;s deployed token…
        </Typography>
      </Box>
    );
  }

  if (!hasProjectToken && tokenReadError) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.4,
              backgroundImage:
                'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Launch Planner
          </Typography>
        </Box>
        <Alert
          severity="error"
          sx={{ borderRadius: 2, maxWidth: 720 }}
          action={
            <Button color="inherit" size="small" onClick={() => reloadPoolSupplyFromChain()} disabled={poolSupplyLoading}>
              Retry
            </Button>
          }
        >
          {tokenReadError}
        </Alert>
      </Box>
    );
  }

  if (!hasProjectToken) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.4,
                backgroundImage:
                  'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Launch Planner
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 820 }}>
              Model pool sizing and bundle buy distribution once your LaunchToken exists.
            </Typography>
          </Box>
        </Box>
        <Alert severity="warning" sx={{ borderRadius: 2, maxWidth: 720 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            No deployed token for this project
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Deploy your token from the Launch / Create token flow first. Until then, pool math (supply and 100% liquidity token side) cannot be inferred and this planner stays disabled — so estimates stay aligned with your real deployment.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.4,
              backgroundImage:
                'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Launch Planner
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 820 }}>
            Model your pool + distribution constraints. We’ll generate buy + funding amounts for wallets you already have in Wallet Management — we never create new keys here.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {computed.initialPrice != null ? (
            <Chip size="small" label={`Initial price: ${computed.initialPrice.toFixed(8)} ETH/token`} />
          ) : (
            <Chip size="small" color="default" variant="outlined" label="Enter pool numbers to preview price" />
          )}
          {results ? (
            <Button variant="outlined" onClick={() => setResultsOpen((v) => !v)} sx={{ borderRadius: 999, px: 2 }}>
              {resultsOpen ? 'Hide results' : 'Show results'}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {tokenReadError ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => reloadPoolSupplyFromChain()} disabled={poolSupplyLoading}>
              Retry
            </Button>
          }
        >
          {tokenReadError}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5} alignItems="flex-start">
        {/* Left: Inputs */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Pool inputs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Token supply comes from your deployed contract. This app assumes you add liquidity with{' '}
                  <strong>100%</strong> of total supply as the token leg (same as Bundle Launch liquidity).
                  {tokenSymbolDisplay ? ` (${tokenSymbolDisplay})` : ''}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1.25}>
                  <Grid item xs={12}>
                    <TextField
                      label="Total token supply (from chain)"
                      name="totalSupply"
                      value={poolSupplyLoading ? 'Loading…' : formData.totalSupply}
                      fullWidth
                      disabled
                      InputProps={{ readOnly: true }}
                      helperText={
                        poolSupplyLoading
                          ? 'Reading totalSupply() from your token contract…'
                          : 'Auto-detected via totalSupply(); not editable.'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Initial tokens in pool (100% of supply)"
                      name="initialTokenPool"
                      value={poolSupplyLoading ? 'Loading…' : formData.initialTokenPool}
                      fullWidth
                      disabled
                      InputProps={{ readOnly: true }}
                      helperText="Fixed to full supply — matches routing 100% of tokens into the pool."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Initial ETH in pool"
                      name="initialEthPool"
                      value={formData.initialEthPool}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ inputMode: 'decimal' }}
                      placeholder="10"
                      disabled={!!tokenReadError || poolSupplyLoading}
                      helperText="ETH amount you intend to seed the pair with (your input)."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Distribution constraints
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Define how much of the supply each wallet can buy.
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Min % per wallet"
                      name="minPercentage"
                      value={formData.minPercentage}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, max: 100, inputMode: 'decimal' }}
                      placeholder="0.10"
                      helperText="0–100"
                      disabled={plannerDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max % per wallet"
                      name="maxPercentage"
                      value={formData.maxPercentage}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, max: 100, inputMode: 'decimal' }}
                      placeholder="1.00"
                      helperText="≥ min, ≤ 100"
                      disabled={plannerDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Target distribution (%)"
                      name="targetPercentage"
                      value={formData.targetPercentage}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, max: 100, inputMode: 'decimal' }}
                      helperText="0–100"
                      disabled={plannerDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Slippage tolerance (%)"
                      name="slippageTolerance"
                      value={formData.slippageTolerance}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, inputMode: 'decimal' }}
                      helperText="Used for wallet funding guidance."
                      disabled={plannerDisabled}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Execution & funding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Add extra ETH per wallet for gas or fixed funding.
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Gas buffer (%)"
                      name="gasBufferPercentage"
                      value={formData.gasBufferPercentage}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, inputMode: 'decimal' }}
                      helperText="Added on top of buy amount."
                      disabled={plannerDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Optional fixed funding per wallet (ETH)"
                      name="fundingAmount"
                      value={formData.fundingAmount}
                      onChange={handleInputChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, inputMode: 'decimal' }}
                      placeholder="0.02"
                      helperText="If set, replaces gas buffer."
                      disabled={plannerDisabled}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right: Summary & actions */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: { lg: 92 } }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Plan actions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Calculate the plan, then apply buy + funding amounts to bundle wallets on this project. If you are short on wallets, generate new keys here (same list as Wallet Management).
                </Typography>
                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1.25}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <CalculateIcon />}
                    onClick={handleCalculate}
                    disabled={loading || plannerDisabled}
                    sx={{ height: 44, borderRadius: 999, justifyContent: 'center' }}
                  >
                    Calculate plan
                  </Button>

                  {results ? (
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {bundleWalletCountLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Bundle wallets on project:{' '}
                            <strong>{bundleWalletCount ?? '—'}</strong>
                            {' · '}
                            plan needs <strong>{Math.max(0, parseInt(results.numWallets, 10) || 0)}</strong>
                          </Typography>
                        )}
                        <Button size="small" variant="text" onClick={() => navigate('/wallets')} sx={{ minWidth: 'auto' }}>
                          Wallet Management
                        </Button>
                      </Stack>
                      {(() => {
                        const need = Math.max(0, parseInt(results.numWallets, 10) || 0);
                        const short =
                          bundleWalletCount === null ? null : Math.max(0, need - bundleWalletCount);
                        return (
                          <Button
                            variant="outlined"
                            startIcon={loading ? <CircularProgress size={18} /> : <VpnKeyIcon />}
                            onClick={handleGenerateMissingBundleWallets}
                            disabled={
                              loading ||
                              plannerDisabled ||
                              bundleWalletCountLoading ||
                              short === null ||
                              short <= 0
                            }
                            sx={{ height: 44, borderRadius: 999 }}
                          >
                            {short === null
                              ? 'Generate new bundle wallets'
                              : short <= 0
                                ? 'Enough bundle wallets for this plan'
                                : `Generate ${short} new bundle wallet${short === 1 ? '' : 's'}`}
                          </Button>
                        );
                      })()}
                    </Stack>
                  ) : null}

                  <Button
                    variant="outlined"
                    onClick={handleUpdateWallets}
                    disabled={loading || !results || plannerDisabled}
                    sx={{ height: 44, borderRadius: 999 }}
                  >
                    Apply plan to wallets
                  </Button>

                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={() => setShowExportDialog(true)}
                        disabled={loading || !results || plannerDisabled}
                        sx={{ height: 44, borderRadius: 999 }}
                      >
                        Export
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          if (!results) return;
                          const { totalSupply, initialEthPool, initialTokenPool } = formData;
                          exportToCSV(
                            results,
                            parseFloat(totalSupply),
                            parseFloat(initialEthPool),
                            parseFloat(initialTokenPool),
                            'distribution_results.csv'
                          );
                        }}
                        disabled={loading || !results || plannerDisabled}
                        sx={{ height: 44, borderRadius: 999 }}
                      >
                        Download CSV
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Plan summary
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Live preview updates as you calculate.
                </Typography>
                <Divider sx={{ my: 1.5 }} />

                {results ? (
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      <b>Wallets:</b> {results.numWallets}
                    </Typography>
                    <Typography variant="body2">
                      <b>Total ETH:</b> {results.totalEthUsed.toFixed(6)}
                    </Typography>
                    <Typography variant="body2">
                      <b>Total tokens:</b> {results.totalTokensDistributed.toFixed(4)}
                    </Typography>
                    <Typography variant="body2">
                      <b>Total price impact:</b> {results.totalPriceImpact.toFixed(2)}%
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Tip: use <strong>Generate … new bundle wallet(s)</strong> above if you need fresh keys, or add them in Wallet Management. Apply only writes buy + funding amounts — it does not mint keys.
                    </Typography>
                  </Stack>
                ) : (
                  <Alert severity="info">Calculate a plan to see summary + wallet breakdown.</Alert>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {results ? (
        <Collapse in={resultsOpen} unmountOnExit>
          <Card variant="outlined" sx={{ mt: 2.5 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Review the wallet plan below.
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <b>Initial price:</b> {results.initialPrice.toFixed(8)} ETH
                  </Typography>
                  <Typography variant="body2">
                    <b>Final price:</b> {results.finalPrice.toFixed(8)} ETH
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <b>Average price:</b> {results.avgPricePerToken.toFixed(8)} ETH
                  </Typography>
                  <Typography variant="body2">
                    <b>Total impact:</b> {results.totalPriceImpact.toFixed(2)}%
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ fontWeight: 900, mt: 1 }}>
                Wallet plan
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 1.25 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                      <TableCell align="right">%</TableCell>
                      <TableCell align="right">ETH to buy</TableCell>
                      <TableCell align="right">Price impact</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.wallets.map((wallet, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell align="right">{wallet.tokens.toFixed(4)}</TableCell>
                        <TableCell align="right">{wallet.tokenPercentage.toFixed(4)}%</TableCell>
                        <TableCell align="right">{wallet.eth.toFixed(6)}</TableCell>
                        <TableCell align="right">{wallet.priceImpact.toFixed(2)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Collapse>
      ) : null}

      {/* Export Dialog */}
      <Dialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
      >
        <DialogTitle>Export Results</DialogTitle>
        <DialogContent>
          <TextField
            label="Filename"
            value={exportFilename}
            onChange={(e) => setExportFilename(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TokenDistributionCalculator; 